import React, { useEffect } from 'react';
import { View, Compass, Move3D } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const MediaPage = () => {
  const { t, language } = useLanguage();

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const texts = {
    heroSubtitle: {
      en: 'IMMERSIVE EXPERIENCE',
      ar: 'تجربة غامرة',
      ku: 'ئەزموونی بەرین'
    },
    heroTitle: {
      en: '360° Virtual Tours',
      ar: 'جولات افتراضية 360°',
      ku: 'گەشتی ڤیرتواڵی 360°'
    },
    heroDesc: {
      en: 'Step inside our projects from anywhere in the world. Experience the quality, design, and atmosphere of our developments through cutting-edge 3D virtual reality technology.',
      ar: 'ادخل إلى مشاريعنا من أي مكان في العالم. اختبر الجودة والتصميم والأجواء في تطويراتنا من خلال تقنية الواقع الافتراضي ثلاثي الأبعاد المتطورة.',
      ku: 'لە هەر شوێنێکی جیهان بچۆ ناو پڕۆژەکانمانەوە. کوالێتی و دیزاین و کەشی گەشەپێدانەکانمان بە تەکنەلۆژیای ڕاستی ڤیرتواڵی سێ ئەستوونی پێشکەوتوو بەژین.'
    },
    whatIs3D: {
      en: 'What is a 3D Virtual Tour?',
      ar: 'ما هي الجولة الافتراضية ثلاثية الأبعاد؟',
      ku: 'گەشتی ڤیرتواڵی سێ ئەستوونی چییە؟'
    },
    whatIs3DDesc: {
      en: 'A 3D virtual tour is an immersive digital experience that allows you to explore our properties as if you were physically there. Using advanced 360° photography and interactive technology, you can navigate through every room, examine details up close, and get a true sense of the space and design quality.',
      ar: 'الجولة الافتراضية ثلاثية الأبعاد هي تجربة رقمية غامرة تتيح لك استكشاف عقاراتنا كما لو كنت موجودًا فعليًا. باستخدام التصوير الفوتوغرافي المتقدم 360 درجة والتكنولوجيا التفاعلية، يمكنك التنقل عبر كل غرفة وفحص التفاصيل عن قرب والحصول على إحساس حقيقي بالمساحة وجودة التصميم.',
      ku: 'گەشتی ڤیرتواڵی سێ ئەستوونی ئەزموونێکی دیجیتاڵی بەرینە کە ڕێگات پێدەدات موڵکەکانمان بگەڕێیت وەک ئەوەی بە فیزیکی لەوێ بیت. بە بەکارهێنانی وێنەگرتنی پێشکەوتووی 360 پلە و تەکنەلۆژیای کارلێکی، دەتوانیت بە هەموو ژوورێکدا بگەڕێیت و وردەکارییەکان لە نزیکەوە بپشکنیت و هەستی ڕاستەقینە بە بۆشایی و کوالێتی دیزاینەکە بکەیت.'
    },
    features: {
      en: [
        { icon: View, title: 'Full 360° View', desc: 'Look around in every direction with complete freedom' },
        { icon: Compass, title: 'Self-Guided Navigation', desc: 'Move through spaces at your own pace and interest' },
        { icon: Move3D, title: 'Interactive Experience', desc: 'Click, drag, and zoom to explore every detail' }
      ],
      ar: [
        { icon: View, title: 'عرض 360° كامل', desc: 'انظر حولك في كل اتجاه بحرية كاملة' },
        { icon: Compass, title: 'تنقل ذاتي التوجيه', desc: 'تحرك عبر المساحات بوتيرتك واهتمامك الخاص' },
        { icon: Move3D, title: 'تجربة تفاعلية', desc: 'انقر واسحب وكبّر لاستكشاف كل التفاصيل' }
      ],
      ku: [
        { icon: View, title: 'بینینی 360° تەواو', desc: 'لە هەموو ئاراستەیەک بە ئازادی تەواو سەیر بکە' },
        { icon: Compass, title: 'گەڕان بە ڕێنمایی خۆت', desc: 'بە خێرایی و حەزی خۆت بە بۆشاییەکاندا بگەڕێ' },
        { icon: Move3D, title: 'ئەزموونی کارلێکی', desc: 'کرتە بکە و ڕابکێشە و زووم بکە بۆ گەڕانی هەموو وردەکارییەک' }
      ]
    },
    availableTours: {
      en: 'Available Virtual Tours',
      ar: 'الجولات الافتراضية المتاحة',
      ku: 'گەشتە ڤیرتواڵییە بەردەستەکان'
    },
    availableToursDesc: {
      en: 'Select a project below to begin your virtual tour experience',
      ar: 'اختر مشروعًا أدناه لبدء تجربة جولتك الافتراضية',
      ku: 'پڕۆژەیەک لە خوارەوە هەڵبژێرە بۆ دەستپێکردنی ئەزموونی گەشتی ڤیرتواڵی'
    },
    startTour: {
      en: 'Start Virtual Tour',
      ar: 'ابدأ الجولة الافتراضية',
      ku: 'گەشتی ڤیرتواڵی دەستپێبکە'
    },
    comingSoon: {
      en: 'Coming Soon',
      ar: 'قريباً',
      ku: 'بەم زووانە'
    }
  };

  const virtualTours = [
    { 
      name: { en: 'Dli Shar', ar: 'دلي شار', ku: 'دڵی شار' }, 
      description: { 
        en: 'Explore our flagship residential project', 
        ar: 'استكشف مشروعنا السكني الرائد', 
        ku: 'پڕۆژەی نیشتەجێبوونی سەرەکیمان بگەڕێ' 
      },
      url: 'https://realsee.ai/P5vvlxp5',
      image: 'https://customer-assets.emergentagent.com/job_37c30a3a-473e-4636-8923-f847e72da520/artifacts/k275g664_7.jpg'
    },
    { 
      name: { en: 'Bakrajo Hills', ar: 'باكراجو هيلز', ku: 'بەکرەجۆ هیلز' }, 
      description: { 
        en: 'Luxury living in the hills', 
        ar: 'حياة فاخرة في التلال', 
        ku: 'ژیانی لوکس لە گردەکان' 
      },
      url: 'https://realsee.ai/bbEEx6vK',
      image: 'https://customer-assets.emergentagent.com/job_37c30a3a-473e-4636-8923-f847e72da520/artifacts/5rq4b5sw_8.png'
    },
    { 
      name: { en: 'Dina City', ar: 'دينا سيتي', ku: 'دینا سیتی' }, 
      description: { 
        en: 'Modern urban living spaces', 
        ar: 'مساحات معيشة حضرية حديثة', 
        ku: 'شوێنی ژیانی شارۆچکەیی مۆدێرن' 
      },
      url: 'https://realsee.ai/ReZZgK2W',
      image: 'https://customer-assets.emergentagent.com/job_37c30a3a-473e-4636-8923-f847e72da520/artifacts/ln64fzd4_c3_Postk.jpg'
    }
  ];

  const features = texts.features[language] || texts.features.en;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="https://images.unsplash.com/photo-1758448500596-ce0e0239f1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxWUiUyMGhlYWRzZXQlMjByZWFsJTIwZXN0YXRlJTIwbHV4dXJ5JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzcxOTE3NDI4fDA&ixlib=rb-4.1.0&q=85"
          alt="Virtual Tours"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light animate-fade-in-up">
            {texts.heroSubtitle[language] || texts.heroSubtitle.en}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-8 animate-fade-in-up delay-200">
            {texts.heroTitle[language] || texts.heroTitle.en}
          </h1>
          <p className="text-lg sm:text-xl text-white/70 font-light animate-fade-in-up delay-300 max-w-3xl mx-auto leading-relaxed">
            {texts.heroDesc[language] || texts.heroDesc.en}
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 animate-bounce-slow">
          <span className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
          <span className="text-xs uppercase tracking-widest text-white/40">
            {language === 'en' ? 'Scroll' : language === 'ar' ? 'تمرير' : 'سکرۆڵ'}
          </span>
        </div>
      </section>

      {/* What is 3D Virtual Tour Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="text-center scroll-reveal">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-8">
              {texts.whatIs3D[language] || texts.whatIs3D.en}
            </h2>
            <p className="text-lg text-white/60 font-light leading-relaxed max-w-3xl mx-auto">
              {texts.whatIs3DDesc[language] || texts.whatIs3DDesc.en}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="scroll-reveal-scale text-center p-8 border border-white/10 hover:border-white/30 transition-all duration-500"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 mx-auto mb-6 border border-white/20 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-white/60" />
                  </div>
                  <h3 className="text-xl font-light text-white mb-4 tracking-wide">
                    {feature.title}
                  </h3>
                  <p className="text-white/50 font-light text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Available Tours Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
          <div className="text-center mb-16 scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              {texts.availableTours[language] || texts.availableTours.en}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              {texts.availableTours[language] || texts.availableTours.en}
            </h2>
            <p className="text-white/60 font-light max-w-2xl mx-auto">
              {texts.availableToursDesc[language] || texts.availableToursDesc.en}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {virtualTours.map((tour, index) => (
              <div
                key={index}
                className="scroll-reveal-scale group relative overflow-hidden"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10" />
                  <img
                    src={tour.image}
                    alt={tour.name[language] || tour.name.en}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* 360 Badge */}
                  <div className="absolute top-4 right-4 z-20">
                    <span className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-light bg-white/10 text-white border border-white/20 backdrop-blur-sm">
                      360°
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 bg-[#111] border border-white/10 border-t-0">
                  <h3 className="text-xl font-light text-white mb-2">
                    {tour.name[language] || tour.name.en}
                  </h3>
                  <p className="text-white/50 font-light text-sm mb-6">
                    {tour.description[language] || tour.description.en}
                  </p>
                  <button
                    onClick={() => tour.url && window.open(tour.url, '_blank')}
                    className={`w-full px-6 py-4 text-xs uppercase tracking-[0.15em] font-light transition-all duration-300 ${
                      tour.url
                        ? 'bg-white text-black hover:bg-white/90'
                        : 'bg-white/5 text-white/30 cursor-not-allowed border border-white/10'
                    }`}
                    disabled={!tour.url}
                  >
                    {tour.url 
                      ? (texts.startTour[language] || texts.startTour.en)
                      : (texts.comingSoon[language] || texts.comingSoon.en)}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0a0a0a] border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6 scroll-reveal">
            {language === 'en' ? 'Ready to Explore?' : language === 'ar' ? 'مستعد للاستكشاف؟' : 'ئامادەیت بۆ گەڕان؟'}
          </h2>
          <p className="text-white/60 font-light mb-10 max-w-2xl mx-auto scroll-reveal">
            {language === 'en' 
              ? 'Contact us today to learn more about our projects or schedule an in-person visit.'
              : language === 'ar'
              ? 'تواصل معنا اليوم لمعرفة المزيد عن مشاريعنا أو لجدولة زيارة شخصية.'
              : 'ئەمڕۆ پەیوەندیمان پێوە بکە بۆ زانینی زیاتر دەربارەی پڕۆژەکانمان یان بۆ ڕێکخستنی سەردانێکی کەسی.'}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-xs uppercase tracking-[0.15em] font-medium hover:bg-white/90 transition-colors scroll-reveal"
          >
            {language === 'en' ? 'Contact Us' : language === 'ar' ? 'تواصل معنا' : 'پەیوەندیمان پێوە بکە'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default MediaPage;
